"use client";

import { Banner, BannerEvent, EventBannerType } from "@/components/Banner";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { FlexColumn, FlexColumnAlignJustifyCenter, FlexRowAlignCenter } from "@/components/Flex";
import FlashcardsCarousel from "@/components/flashcards/FlashcardsCarousel";
import { ASSETS_URL } from "@/config/deployConstants";
import { AuthEvent } from "@/features/Auth/AuthPopup/utils/constants";
import { useCheckAuth } from "@/hooks/useCheckAuth";
import { borderRadius } from "@/utils/borderRadius";
import { themeColors } from "@/utils/themeColors";
import { StudySessionProgressEnum, StudySessionType } from "@knowt/syncing/graphql/schema";
import { useFlashcardSet } from "@knowt/syncing/hooks/flashcards/useFlashcards";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { createQueryStrings } from "@knowt/syncing/utils/genericUtils";
import Box from "@mui/material/Box";
import Image from "next/image";
import { useRouter } from "@bprogress/next";
import toast from "react-hot-toast";
import { mutate } from "swr";
import { SetOverviewViewStates } from "../types";
import BorderedIconBtn from "@/components/CircularButton/styled/BorderedIconBtn";
import Mountable from "@/components/styled/div/Mountable";
import { useState } from "react";
import dynamic from "next/dynamic";
import LazyLoaded from "@/components/LazyLoaded";

const StudyModeRestrictionPopup = dynamic(() => import("@/components/popupWithSidebar/StudyModeRestrictionPopup"));

const flashcardsImg = `${ASSETS_URL}/images/flashcard-icon.svg`;
const brainImg = `${ASSETS_URL}/images/brain.png`;
const examImg = `${ASSETS_URL}/images/ExamIcon.png`;
const spacedRepetitionImg = `${ASSETS_URL}/images/Spaced_Repetition.png`;
const heartPuzzleImg = `${ASSETS_URL}/images/heart_puzzle.png`;

const StudyButton = ({
    image,
    alt,
    label,
    onClick,
    isDisabled,
    studySessionType,
    isAllowSettings,
    flashcardSetId,
}: {
    image: string;
    alt: string;
    label: string;
    onClick: () => void;
    isDisabled: boolean;
    studySessionType: StudySessionType;
    isAllowSettings: boolean;
    flashcardSetId: string;
}) => {
    const { isStudent } = useCurrentUser();
    const [isStudySettingsOpen, setIsStudySettingsOpen] = useState(false);

    const handleOnClick = () => {
        if (isDisabled) {
            return toast.error(
                isStudent
                    ? "Sorry, your teacher disabled this for this set"
                    : "This study mode is disabled for this set"
            );
        }
        onClick();
    };

    return (
        <>
            <CircularRectTextButton
                component={Box}
                onClick={handleOnClick}
                disabled={isDisabled}
                sx={{
                    flexDirection: "column",
                    gap: "1.2rem",
                    padding: "1.3rem",
                    width: "fit-content",
                    backgroundColor: isDisabled ? themeColors.neutral1 : themeColors.neutralWhite,
                    borderRadius: borderRadius.card,
                    "&:hover": {
                        // backgroundColor: themeColors.neutral1,
                        outline: `1px solid ${themeColors.neutralBlack}`,
                    },
                    "&:active": { transform: "scale(0.99)" },
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    color: isDisabled ? themeColors.neutral3 : themeColors.neutralBlack,
                    flex: `1 0 auto`,
                }}>
                <FlexRowAlignCenter style={{ gap: "1.2rem" }}>
                    <Image src={image} width={35} height={35} alt={alt} style={{ objectFit: "contain" }} />
                    <span className="bodyBold2">{label}</span>
                </FlexRowAlignCenter>
                <Mountable mount={isAllowSettings}>
                    <FlexRowAlignCenter style={{ gap: "1rem" }}>
                        <BorderedIconBtn
                            onClick={e => {
                                e.stopPropagation();
                                setIsStudySettingsOpen(true);
                            }}
                            icon="Settings2"
                        />
                        <BorderedIconBtn
                            icon="ArrowRight"
                            onClick={e => {
                                e.stopPropagation();
                                handleOnClick();
                            }}
                        />
                    </FlexRowAlignCenter>
                </Mountable>
            </CircularRectTextButton>

            <LazyLoaded load={isStudySettingsOpen}>
                <StudyModeRestrictionPopup
                    isOpen={isStudySettingsOpen}
                    onClose={() => setIsStudySettingsOpen(false)}
                    initialTab={studySessionType}
                    flashcardSetId={flashcardSetId}
                />
            </LazyLoaded>
        </>
    );
};

const SetOverviewStudyBtnWithCarousel = ({
    userId,
    flashcardSetId,
    setViewStates,
}: { userId?: string; flashcardSetId: string; setViewStates: SetOverviewViewStates }) => {
    const router = useRouter();

    const { checkAuth } = useCheckAuth();
    const { isStudent } = useCurrentUser();
    const { flashcardSet } = useFlashcardSet({ flashcardSetId });

    const readOnly = flashcardSet?.userId !== userId;

    const blockedStudyModes = flashcardSet?.studySettings?.blockedStudyModes;
    const studyRestrictionsExist = readOnly && isStudent && !!flashcardSet?.classId && !!blockedStudyModes?.length;

    const handleLearnMode = async (studyFrom?: StudySessionProgressEnum) => {
        await mutate(["round-data", userId, flashcardSetId, StudySessionType.LEARN], null);
        router.push(`/study/flashcards/${flashcardSetId}/learn${createQueryStrings({ studyFrom })}`);
    };

    const handleTestMode = async () => {
        await mutate(["round-data", userId, flashcardSetId, StudySessionType.TEST], null, { revalidate: false });
        router.push(`/study/flashcards/${flashcardSetId}/test`);
    };

    const handleSpacedRepetitionMode = async () => {
        await mutate(["round-data", userId, flashcardSetId, StudySessionType.SPACED], null, { revalidate: false });
        router.push(`/study/flashcards/${flashcardSetId}/spaced`);
    };

    const handleMatchMode = async () => {
        await mutate(["round-data", userId, flashcardSetId, StudySessionType.MATCH], null, { revalidate: false });
        router.push(`/study/flashcards/${flashcardSetId}/match`);
    };

    const handleReviewMode = async () => {
        await mutate(["round-data", userId, flashcardSetId, StudySessionType.REVIEW], null, { revalidate: false });
        router.push(`/study/flashcards/${flashcardSetId}/review`);
    };

    const studyBtnList: React.ComponentProps<typeof StudyButton>[] = [
        {
            studySessionType: StudySessionType.LEARN,
            image: brainImg,
            alt: "learn",
            label: "Learn",
            onClick: () => {
                checkAuth({
                    event: AuthEvent.STUDY_FLASHCARDS_LEARN_MODE,
                    onAuthSuccess: () => handleLearnMode(),
                });
            },
            isDisabled: studyRestrictionsExist && blockedStudyModes.includes(StudySessionType.LEARN),
            isAllowSettings: setViewStates.isTeacherViewClassSet,
            flashcardSetId: flashcardSetId,
        },
        {
            studySessionType: StudySessionType.TEST,
            image: examImg,
            alt: "exam",
            label: "Practice Test",
            onClick: () => {
                checkAuth({
                    event: AuthEvent.STUDY_FLASHCARDS_TEST_MODE,
                    onAuthSuccess: () => handleTestMode(),
                });
            },
            isDisabled: studyRestrictionsExist && blockedStudyModes.includes(StudySessionType.TEST),
            isAllowSettings: setViewStates.isTeacherViewClassSet,
            flashcardSetId: flashcardSetId,
        },
        {
            studySessionType: StudySessionType.SPACED,
            image: spacedRepetitionImg,
            alt: "spaced repetition",
            label: "Spaced Repetition",
            onClick: () => {
                checkAuth({
                    event: AuthEvent.STUDY_FLASHCARDS_SPACED_MODE,
                    onAuthSuccess: () => handleSpacedRepetitionMode(),
                });
            },
            isDisabled: studyRestrictionsExist && blockedStudyModes.includes(StudySessionType.SPACED),
            isAllowSettings: setViewStates.isTeacherViewClassSet,
            flashcardSetId: flashcardSetId,
        },
        {
            studySessionType: StudySessionType.MATCH,
            image: heartPuzzleImg,
            alt: "heart puzzle",
            label: "Match",
            onClick: () => {
                checkAuth({
                    event: AuthEvent.STUDY_FLASHCARDS_MATCH_MODE,
                    onAuthSuccess: () => handleMatchMode(),
                });
            },
            isDisabled: studyRestrictionsExist && blockedStudyModes.includes(StudySessionType.MATCH),
            isAllowSettings: setViewStates.isTeacherViewClassSet,
            flashcardSetId: flashcardSetId,
        },
        {
            studySessionType: StudySessionType.REVIEW,
            image: flashcardsImg,
            alt: "flashcards",
            label: "Flashcards",
            onClick: () => {
                checkAuth({
                    event: AuthEvent.STUDY_FLASHCARDS_REVIEW_MODE,
                    onAuthSuccess: () => handleReviewMode(),
                });
            },
            isDisabled: false,
            isAllowSettings: false,
            flashcardSetId: flashcardSetId,
        },
    ];

    return (
        <FlexColumnAlignJustifyCenter style={{ flex: 1, gap: "4rem" }}>
            {studyRestrictionsExist && (
                <Banner
                    type={EventBannerType.ERROR}
                    event={BannerEvent.FLASHCARDSET_DISABLED_OPTIONS}
                    style={{ width: "100%" }}
                    labelProps={{ className: "bodyBold2" }}
                />
            )}
            <FlexColumn style={{ flex: 1, gap: "2.4rem 4rem", width: "100%" }}>
                <FlexRowAlignCenter style={{ gap: "2rem", flexWrap: "wrap", alignItems: "stretch" }}>
                    {studyBtnList.map(
                        ({ label, onClick, alt, image, isDisabled, studySessionType, isAllowSettings }, i) => (
                            <StudyButton
                                key={i + label}
                                label={label}
                                image={image}
                                alt={alt}
                                onClick={onClick}
                                isDisabled={isDisabled}
                                studySessionType={studySessionType}
                                isAllowSettings={isAllowSettings}
                                flashcardSetId={flashcardSetId}
                            />
                        )
                    )}
                </FlexRowAlignCenter>
                <FlashcardsCarousel fromOverview />
            </FlexColumn>
        </FlexColumnAlignJustifyCenter>
    );
};

export default SetOverviewStudyBtnWithCarousel;
